import { createAuthClient } from 'better-auth/react';
import { adminClient, inferAdditionalFields } from 'better-auth/client/plugins';

/**
 * Cliente de autenticação. Fala com as rotas `/auth/*` da API, que são servidas
 * pelo BetterAuth e por isso não aparecem no `openapi.json`.
 *
 * O plugin admin entra sem `ac`/`roles` de propósito: verificação de permissão
 * que vale é a do servidor (G-POLICY). Aqui o papel serve apenas para decidir o
 * que mostrar, nunca para autorizar.
 *
 * Os campos extras do usuário são **declarados**, e não inferidos do tipo do
 * servidor: front e API são pacotes separados, e importar o tipo de `auth.ts`
 * puxaria o Drizzle e o segredo de sessão para dentro do bundle do navegador.
 * O preço é manter esta lista de olho na do `auth.ts` da API - hoje, o celular
 * e as três colunas das boas-vindas.
 */
export function createAuth(baseURL: string) {
  return createAuthClient({
    baseURL,
    basePath: '/auth',
    plugins: [
      adminClient(),
      inferAdditionalFields({
        user: {
          phone: { type: 'string', required: false },
          onboardedAt: { type: 'date', required: false },
          focusModules: { type: 'string[]', required: false },
          onboardingDismissals: { type: 'number', required: false },
        },
      }),
    ],
  });
}

export type AuthClient = ReturnType<typeof createAuth>;

/**
 * As etapas que o aluno pode declarar estar estudando.
 *
 * Espelha o `moduleEnum` do banco. Mora aqui, e não no app, porque quem grava
 * é o `authClient.updateUser` deste pacote e quem confere é o servidor: manter
 * os três nomes ao lado da declaração do campo é o que evita a lista solta.
 */
export const FOCUS_MODULES = ['enac', 'objective', 'essay'] as const;
export type FocusModule = (typeof FOCUS_MODULES)[number];

/**
 * As etapas gravadas, ficando só as que existem.
 *
 * Virou lista em 05/09/2026: a 1ª e a 2ª fase ainda não têm questão comentada
 * e não se vendem, mas quantos as querem é o que decide o que comentar depois -
 * e para isso a pergunta aceita mais de uma resposta.
 */
export function focosValidos(bruto: unknown): FocusModule[] {
  if (!Array.isArray(bruto)) return [];
  return bruto.filter((m): m is FocusModule => FOCUS_MODULES.includes(m as FocusModule));
}

/** Papéis do backoffice, na ordem de menor para maior autonomia. */
export const ROLES = ['user', 'admin', 'publisher', 'owner'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  user: 'Aluno',
  admin: 'Administrador',
  publisher: 'Administrador com publicação',
  owner: 'Dono',
};

export const ROLE_DESCRIPTION: Record<Role, string> = {
  user: 'Acessa a plataforma de estudos, sem acesso ao backoffice.',
  admin: 'Cadastra e edita provas e questões, mas não publica.',
  publisher: 'Cadastra, edita e publica questões para os alunos.',
  owner: 'Publica questões e administra os demais administradores.',
};

/** Papéis com acesso ao backoffice. */
export const ADMIN_ROLES: readonly Role[] = ['admin', 'publisher', 'owner'];

export const isAdminRole = (role: string | null | undefined): boolean =>
  ADMIN_ROLES.includes(role as Role);
